/**
 * "What I missed": the level's slips, opened from the accuracy row on Results.
 *
 * Three short lists, kindest framing first: the words sent wrong with what was
 * typed (the letters that differ picked out), the words that slipped but were
 * fixed before sending, and the keys that slipped most. An overlay rather than
 * a screen, so the player looks and is straight back on Results.
 *
 * Closes with its Close button, a click outside the card, Esc, Enter or Space.
 */
import Phaser from 'phaser';
import { fixedWordsOf, missedWordsOf, trickyKeysOf } from '../../systems/missReview';
import type { MissedWord } from '../../systems/missReview';
import type { WordSlip } from '../../types';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../../types';
import { pressButton, styleButton } from './menuButton';

const DEPTH = 300;
const MISSED_SHOWN = IS_PORTRAIT ? 6 : 4;
const FIXED_SHOWN = 14;

export class MissesPanel {
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly onKey: (event: KeyboardEvent) => void;
  private readonly left: number;
  private readonly width: number;

  constructor(
    private readonly scene: Phaser.Scene,
    slips: readonly WordSlip[],
    letters: Readonly<Record<string, number>>,
    private readonly onClose: () => void,
  ) {
    this.width = IS_PORTRAIT ? GAME_WIDTH - 32 : 900;
    const cx = GAME_WIDTH / 2;
    this.left = cx - this.width / 2;

    // Laid out from y = 0, then the card is sized to fit and the whole thing
    // centred, so a short list gets a small card rather than a tall empty one.
    const content: Phaser.GameObjects.Text[] = [];
    const add = (text: Phaser.GameObjects.Text): Phaser.GameObjects.Text => {
      content.push(text);
      return text;
    };
    add(this.text(cx, 48, 'What you missed', 40, PALETTE.snow).setOrigin(0.5));

    let y = 110;
    const missed = missedWordsOf(slips);
    if (missed.length > 0) {
      add(this.text(this.left + 40, y, 'Words to practise', 26, PALETTE.gold));
      y += 48;
      for (const word of missed.slice(0, MISSED_SHOWN)) {
        content.push(...this.drawMissedWord(word, y));
        y += IS_PORTRAIT ? 48 : 42;
      }
      if (missed.length > MISSED_SHOWN) {
        add(
          this.text(this.left + 40, y, `and ${missed.length - MISSED_SHOWN} more`, 20, PALETTE.ice),
        );
        y += 34;
      }
      y += 16;
    }

    const fixed = fixedWordsOf(slips);
    if (fixed.length > 0) {
      add(this.text(this.left + 40, y, 'Slipped, but you fixed them', 26, PALETTE.gold));
      y += 44;
      const shown = fixed.slice(0, FIXED_SHOWN).join('     ');
      const more = fixed.length > FIXED_SHOWN ? `     and ${fixed.length - FIXED_SHOWN} more` : '';
      const list = add(this.text(this.left + 40, y, shown + more, 24, PALETTE.snow));
      list
        .setWordWrapWidth(this.width - 80)
        .setLineSpacing(8)
        .setMaxLines(2);
      y += list.height + 26;
    }

    const keys = trickyKeysOf(letters);
    if (keys !== '') {
      add(this.text(this.left + 40, y, 'Keys that slipped', 26, PALETTE.gold));
      y += 44;
      add(this.text(this.left + 40, y, keys, 26, PALETTE.snow));
      y += 44;
    }

    const height = Math.min(GAME_HEIGHT - 40, y + 100);
    const top = Math.round((GAME_HEIGHT - height) / 2);
    const close = add(this.text(cx, height - 48, 'Close', 28, PALETTE.snow))
      .setOrigin(0.5)
      .setBackgroundColor('#1b2a41')
      .setPadding(28, 10, 28, 10)
      .setInteractive({ useHandCursor: true });
    close.on('pointerup', () => pressButton(close, () => this.close()));
    styleButton(close, true);
    for (const text of content) text.setY(text.y + top).setDepth(DEPTH + 1);

    // The dim layer closes on a click; the card above it swallows its own.
    const cy = top + height / 2;
    const shade = scene.add
      .rectangle(cx, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x05080f, 0.78)
      .setInteractive();
    shade.on('pointerup', () => this.close());
    const card = scene.add.rectangle(cx, cy, this.width, height, 0x000000, 0).setInteractive();
    const panel = scene.add.graphics();
    panel.fillStyle(0x16233a, 1);
    panel.fillRoundedRect(this.left, top, this.width, height, 28);
    panel.lineStyle(4, 0x8ecae6, 1);
    panel.strokeRoundedRect(this.left, top, this.width, height, 28);
    for (const object of [shade, card, panel]) object.setDepth(DEPTH);
    this.objects.push(shade, card, panel);

    this.onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        pressButton(close, () => this.close());
      }
    };
    scene.input.keyboard?.on('keydown', this.onKey);
  }

  close(): void {
    this.scene.input.keyboard?.off('keydown', this.onKey);
    for (const object of this.objects) object.destroy();
    this.objects.length = 0;
    this.onClose();
  }

  /** "friend    you typed  f r e i n d", the letters that differ in coral. */
  private drawMissedWord(missed: MissedWord, y: number): Phaser.GameObjects.Text[] {
    const times = missed.times > 1 ? `  ×${missed.times}` : '';
    const drawn = [this.text(this.left + 40, y, missed.word + times, 30, PALETTE.snow)];
    const column = this.left + 300;
    const label = this.text(column, y + 6, 'you typed', 20, PALETTE.ice);
    drawn.push(label);
    let x = column + label.width + 14;
    if (missed.typed === '') {
      drawn.push(this.text(x, y, '(nothing)', 26, PALETTE.ice));
      return drawn;
    }
    for (let i = 0; i < missed.typed.length; i += 1) {
      const char = missed.typed[i] ?? '';
      const wrong = char !== missed.word[i];
      const letter = this.text(x, y + 2, char, 28, wrong ? PALETTE.sunset : PALETTE.ice);
      if (wrong) letter.setFontStyle('bold');
      drawn.push(letter);
      x += letter.width + 1;
    }
    return drawn;
  }

  private text(
    x: number,
    y: number,
    content: string,
    size: number,
    color: string,
  ): Phaser.GameObjects.Text {
    const text = this.scene.add.text(x, y, content, {
      fontFamily: FONT_FAMILY,
      fontSize: `${size}px`,
      color,
    });
    this.objects.push(text);
    return text;
  }
}
